import { getOperationalOrdersState, saveOperationalOrdersState } from "../local/operationalOrders";
import { getRestaurantProfile } from "../local/restaurantProfile";
import {
  orderRequiresKitchen,
  resolveOrderKitchenModeAtDispatch,
} from "../local/orderModel";
import {
  fetchSharedOperationalState,
  pushSharedOperationalState,
} from "./operationalStateService";
import { getBranchPublicProfile } from "./companyAdminService";
import { getBranchMenuSnapshot } from "./menuService";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "../local/runtimeEnvironment";
import { supabase } from "./client";
import { isSupabaseConfigured } from "./config";

const PHASE1_PATCH_FILE = "external_orders_phase1_patch.sql";
const WHATSAPP_DELIVERY_PHASE2_PATCH_FILE =
  "external_orders_whatsapp_delivery_phase2_patch.sql";
const DELIVERY_DISTANCE_RATES_PATCH_FILE =
  "delivery_distance_rates_phase5_patch.sql";
const MANUAL_DELIVERY_FEE_PATCH_FILE =
  "external_orders_manual_delivery_fee_phase6_patch.sql";
const WE_RIDER_DISPATCH_MIGRATION =
  "20260817193000_we_delivery_dispatch_modes.sql";

export const EXTERNAL_ORDERS_UPDATED_EVENT = "mozzoapp:external-orders-updated";

export const subscribeToExternalOrderChanges = (branchId, listener) => {
  const safeBranchId = String(branchId || "").trim();

  if (!isSupabaseConfigured || !supabase || !safeBranchId) {
    return () => {};
  }

  const notify = () => {
    if (typeof listener === "function") listener();
  };
  const channel = supabase
    .channel(`external-orders:${safeBranchId}:${Date.now()}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "external_orders",
        filter: `branch_id=eq.${safeBranchId}`,
      },
      notify
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "we_delivery_assignments",
        filter: `branch_id=eq.${safeBranchId}`,
      },
      notify
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
};

const EXTERNAL_ORDER_STATUS_META = {
  draft: { label: "Borrador", tone: "muted" },
  pending_confirmation: { label: "Pendiente de confirmación", tone: "warning" },
  received: { label: "Ingresado", tone: "info" },
  pending_payment: { label: "Esperando pago", tone: "warning" },
  confirmed: { label: "Confirmado", tone: "success" },
  preparing: { label: "En preparación", tone: "info" },
  ready_for_pickup: { label: "Listo para retirar", tone: "success" },
  ready_for_delivery: { label: "Listo para envío", tone: "success" },
  assigned_to_driver: { label: "Repartidor asignado", tone: "info" },
  out_for_delivery: { label: "En camino", tone: "info" },
  delivered: { label: "Entregado", tone: "success" },
  picked_up: { label: "Retirado", tone: "success" },
  cancelled: { label: "Cancelado", tone: "danger" },
  rejected: { label: "Rechazado", tone: "danger" },
  refunded: { label: "Reintegrado", tone: "danger" },
};

const EXTERNAL_PAYMENT_STATUS_META = {
  pending: "Pendiente",
  awaiting_confirmation: "Esperando confirmación",
  paid: "Cobrado",
  rejected: "Rechazado",
  refunded: "Reintegrado",
  cancelled: "Cancelado",
};

const PAYMENT_METHOD_LABELS = {
  cash: "Efectivo ahora",
  cash_on_delivery: "Pagar al Rider",
  qr_on_delivery: "QR al entregar",
  card_on_delivery: "Tarjeta al entregar",
  mercado_pago: "Mercado Pago",
  transfer: "Transferencia",
  digital: "Pago digital",
  other: "Otro medio",
};

const SOURCE_CHANNEL_LABELS = {
  manual: "Carga manual",
  menu_web: "Menú web",
  chatbot: "Chatbot",
  whatsapp: "WhatsApp",
  we_pedidos: "We! pedidos",
};

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Supabase no está configurado para usar Pedidos.");
  }
};

const getErrorText = (error) =>
  `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`
    .toLowerCase()
    .trim();

const isPhaseOneSchemaMissing = (error) => {
  const message = getErrorText(error);

  return (
    error?.code === "42P01" ||
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("external_") ||
    message.includes("create_external_order") ||
    message.includes("transition_external_order_status")
  );
};

const isWhatsAppDeliveryPhaseTwoSchemaMissing = (error) => {
  const message = getErrorText(error);

  return [
    "business_phone_e164",
    "meta_phone_number_id",
    "meta_business_account_id",
    "delivery_menu_enabled",
    "chatbot_enabled",
    "delivery_public_token",
    "webhook_status",
    "delivery_zone_id",
    "delivery_fee_override",
  ].some((column) => message.includes(column));
};

const isMissingDistanceRatesTableError = (error) => {
  const message = getErrorText(error);

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes("external_delivery_distance_rates")
  );
};

const isMissingManualDeliveryFeeFunction = (error) => {
  const message = getErrorText(error);
  return message.includes("create_external_order_with_delivery_fee");
};

const isMissingWeRiderDispatchFunction = (error) =>
  getErrorText(error).includes("offer_external_order_to_we_riders");

const isMissingAtomicKitchenDispatchFunction = (error) => {
  const message = getErrorText(error);
  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("dispatch_external_order_to_kitchen")
  );
};

const normalizeServiceError = (error, fallbackMessage) => {
  if (isMissingWeRiderDispatchFunction(error)) {
    return new Error(
      `Falta aplicar ${WE_RIDER_DISPATCH_MIGRATION} en Supabase para habilitar la asignacion automatica.`
    );
  }

  if (isMissingManualDeliveryFeeFunction(error)) {
    return new Error(
      `Falta aplicar ${MANUAL_DELIVERY_FEE_PATCH_FILE} en Supabase para guardar el costo de envio manual.`
    );
  }

  if (isWhatsAppDeliveryPhaseTwoSchemaMissing(error)) {
    return new Error(
      `Falta aplicar ${WHATSAPP_DELIVERY_PHASE2_PATCH_FILE} en Supabase para habilitar WhatsApp Delivery.`
    );
  }

  if (isPhaseOneSchemaMissing(error)) {
    return new Error(
      `Falta aplicar ${PHASE1_PATCH_FILE} en Supabase para habilitar Pedidos externos.`
    );
  }

  return new Error(error?.message || fallbackMessage);
};

const cleanText = (value, maxLength = 160) => String(value || "").trim().slice(0, maxLength);

export const normalizeExternalPhone = (value) => cleanText(value, 60).replace(/\D/g, "");

export const formatExternalPhone = (value) => {
  const digits = normalizeExternalPhone(value);

  if (!digits) {
    return "Sin teléfono";
  }

  if (digits.length === 10) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return digits;
};

export const getExternalOrderStatusMeta = (status) =>
  EXTERNAL_ORDER_STATUS_META[status] || {
    label: cleanText(status || "Sin estado", 80),
    tone: "muted",
  };

export const getExternalPaymentStatusLabel = (status) =>
  EXTERNAL_PAYMENT_STATUS_META[status] || cleanText(status || "Pendiente", 80);

export const getExternalPaymentMethodLabel = (method) =>
  PAYMENT_METHOD_LABELS[method] || cleanText(method || "Sin definir", 80);

export const getExternalSourceChannelLabel = (source) =>
  SOURCE_CHANNEL_LABELS[source] || cleanText(source || "Manual", 80);

export const calculateExternalDeliveryQuote = ({ subtotal = 0, zone = null }) => {
  const safeSubtotal = Math.max(0, Number(subtotal || 0));
  const minimumOrder = Math.max(0, Number(zone?.min_order ?? zone?.minOrder ?? 0));
  const freeFrom = Math.max(0, Number(zone?.free_from ?? zone?.freeFrom ?? 0));
  const standardFee = Math.max(0, Number(zone?.flat_fee ?? zone?.flatFee ?? 0));
  const meetsMinimum = !zone || safeSubtotal >= minimumOrder;
  const deliveryFee =
    !zone || (freeFrom > 0 && safeSubtotal >= freeFrom) ? 0 : standardFee;

  return {
    subtotal: safeSubtotal,
    minimumOrder,
    freeFrom,
    deliveryFee,
    meetsMinimum,
    amountMissingForMinimum: Math.max(0, minimumOrder - safeSubtotal),
    amountMissingForFreeDelivery: freeFrom > 0 ? Math.max(0, freeFrom - safeSubtotal) : 0,
    total: safeSubtotal + deliveryFee,
  };
};

export const calculateExternalCartSummary = ({ items = [], fulfillmentType = "pickup", zone }) => {
  const subtotal = (Array.isArray(items) ? items : []).reduce(
    (total, item) =>
      total +
      Math.max(0, Number(item?.unitPrice ?? item?.price ?? 0)) *
        Math.max(1, Number(item?.quantity || 1)),
    0
  );
  const quote =
    fulfillmentType === "delivery"
      ? calculateExternalDeliveryQuote({ subtotal, zone })
      : calculateExternalDeliveryQuote({ subtotal, zone: null });

  return {
    ...quote,
    itemCount: (Array.isArray(items) ? items : []).reduce(
      (total, item) => total + Math.max(1, Number(item?.quantity || 1)),
      0
    ),
  };
};

export const resolveExternalDeliveryDistanceRate = (rates = [], distanceMeters = null) => {
  if (distanceMeters === "" || distanceMeters == null) return null;
  const distance = Number(distanceMeters);
  if (!Number.isFinite(distance) || distance < 0) return null;

  const activeRates = (Array.isArray(rates) ? rates : [])
    .filter((rate) => rate?.active !== false)
    .sort(
      (left, right) =>
        Number(left?.min_distance_meters || 0) - Number(right?.min_distance_meters || 0)
    );

  return (
    activeRates.find((rate, index) => {
      const minimum = Math.max(0, Number(rate?.min_distance_meters || 0));
      const maximum = Math.max(0, Number(rate?.max_distance_meters || 0));
      const isLastRate = index === activeRates.length - 1;
      return distance >= minimum && (distance < maximum || (isLastRate && distance <= maximum));
    }) || null
  );
};

export const calculateExternalCashChange = ({ total = 0, cashReceived = 0 }) => {
  const safeTotal = Math.max(0, Number(total || 0));
  const safeCashReceived = Math.max(0, Number(cashReceived || 0));

  return {
    cashReceived: safeCashReceived,
    missing: Math.max(0, safeTotal - safeCashReceived),
    change: Math.max(0, safeCashReceived - safeTotal),
  };
};

export const canTransitionExternalOrder = (fromStatus, nextStatus) => {
  if (fromStatus === nextStatus) {
    return true;
  }

  const transitions = {
    draft: ["confirmed", "preparing", "cancelled", "rejected"],
    pending_confirmation: ["confirmed", "preparing", "cancelled", "rejected"],
    received: ["confirmed", "preparing", "cancelled", "rejected"],
    pending_payment: ["confirmed", "preparing", "cancelled", "rejected"],
    confirmed: ["preparing", "ready_for_pickup", "ready_for_delivery", "cancelled"],
    preparing: ["ready_for_pickup", "ready_for_delivery", "cancelled"],
    ready_for_pickup: ["picked_up", "cancelled"],
    ready_for_delivery: ["assigned_to_driver", "out_for_delivery", "cancelled"],
    assigned_to_driver: ["out_for_delivery", "cancelled"],
    out_for_delivery: ["delivered", "cancelled"],
  };

  return (transitions[fromStatus] || []).includes(nextStatus);
};

const toCustomerPayload = (customer = {}, branchId) => ({
  company_id: customer.companyId,
  created_branch_id: customer.createdBranchId || branchId,
  first_name: cleanText(customer.firstName, 80),
  last_name: cleanText(customer.lastName, 80) || null,
  phone_original: cleanText(customer.phone, 60),
  phone_normalized: normalizeExternalPhone(customer.phone),
  phone_e164: normalizeExternalPhone(customer.phone)
    ? `+${normalizeExternalPhone(customer.phone)}`
    : null,
  email: cleanText(customer.email, 160) || null,
  status: customer.status || "active",
  marketing_opt_in: customer.marketingOptIn === true,
  marketing_opt_in_at: customer.marketingOptIn === true ? new Date().toISOString() : null,
  marketing_opt_in_source:
    customer.marketingOptIn === true ? "manual_phase1" : null,
  internal_notes: cleanText(customer.internalNotes, 1200) || null,
  updated_at: new Date().toISOString(),
});


const createExternalDeliveryPublicToken = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "");
  }

  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 18)}`.slice(0, 48);
};

export const buildExternalDeliveryPublicUrl = (deliveryToken) => {
  const safeToken = cleanText(deliveryToken, 80);

  if (!safeToken) return "";

  const location = typeof window !== "undefined" ? window.location : null;
  const hostname = String(location?.hostname || "").toLowerCase();
  const publicHostname = hostname === "backoffice.beta.mozzoapp.com"
    ? "beta.mozzoapp.com"
    : hostname === "backoffice.mozzoapp.com"
      ? "mozzoapp.com"
      : hostname;
  const origin = publicHostname
    ? `${location?.protocol || "https:"}//${publicHostname}`
    : "https://mozzoapp.com";

  return `${origin}/delivery/${encodeURIComponent(safeToken)}`;
};

export const listExternalCustomers = async ({ companyId, search = "", limit = 100 }) => {
  ensureSupabaseReady();
  let request = supabase
    .from("external_customers")
    .select("*, addresses:external_customer_addresses(*)")
    .eq("company_id", companyId)
    .eq("status", "active")
    .order("last_order_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .limit(Math.max(1, Math.min(300, Number(limit || 100))));
  const normalizedSearch = cleanText(search, 80);

  if (normalizedSearch) {
    const safeSearch = normalizedSearch.replace(/[,%()]/g, " ").trim();
    request = request.or(
      `first_name.ilike.%${safeSearch}%,last_name.ilike.%${safeSearch}%,phone_normalized.ilike.%${normalizeExternalPhone(safeSearch)}%`
    );
  }

  const { data, error } = await request;
  if (error) {
    throw normalizeServiceError(error, "No se pudo cargar la cartera de clientes.");
  }

  return Array.isArray(data) ? data : [];
};

export const saveExternalCustomer = async ({ companyId, branchId, customer }) => {
  ensureSupabaseReady();
  // Reusing a phone number should make the customer available again after an archive.
  const payload = {
    ...toCustomerPayload({ ...customer, companyId }, branchId),
    status: "active",
  };

  if (!payload.first_name || payload.phone_normalized.length < 6) {
    throw new Error("Indica nombre y teléfono válido para guardar el cliente.");
  }

  const { data, error } = await supabase
    .from("external_customers")
    .upsert(payload, { onConflict: "company_id,phone_normalized" })
    .select("*")
    .single();
  if (error) {
    throw normalizeServiceError(error, "No se pudo guardar el cliente.");
  }

  return data;
};

export const removeExternalCustomer = async ({ companyId, customerId }) => {
  ensureSupabaseReady();

  const { count, error: orderError } = await supabase
    .from("external_orders")
    .select("id", { count: "exact", head: true })
    .eq("company_id", companyId)
    .eq("customer_id", customerId);
  if (orderError) {
    throw normalizeServiceError(orderError, "No se pudo comprobar el historial del cliente.");
  }

  const hasOrderHistory = Number(count || 0) > 0;
  const request = hasOrderHistory
    ? supabase
        .from("external_customers")
        .update({ status: "archived", updated_at: new Date().toISOString() })
        .eq("id", customerId)
        .eq("company_id", companyId)
    : supabase
        .from("external_customers")
        .delete()
        .eq("id", customerId)
        .eq("company_id", companyId);
  const { error } = await request;
  if (error) {
    throw normalizeServiceError(error, "No se pudo eliminar el cliente.");
  }

  return { archived: hasOrderHistory };
};

export const saveExternalCustomerAddress = async ({ customerId, branchId, address }) => {
  ensureSupabaseReady();
  const payload = {
    customer_id: customerId,
    branch_id: branchId,
    label: cleanText(address.label || "Principal", 60),
    is_default: address.isDefault !== false,
    street: cleanText(address.street, 160),
    street_number: cleanText(address.streetNumber, 40) || null,
    floor: cleanText(address.floor, 30) || null,
    unit: cleanText(address.unit, 30) || null,
    neighborhood: cleanText(address.neighborhood, 100) || null,
    city: cleanText(address.city, 100) || null,
    province: cleanText(address.province, 100) || null,
    postal_code: cleanText(address.postalCode, 30) || null,
    reference: cleanText(address.reference, 400) || null,
    updated_at: new Date().toISOString(),
  };

  if (!payload.street) {
    throw new Error("La calle es obligatoria para guardar una dirección.");
  }

  const request = address.id
    ? supabase.from("external_customer_addresses").update(payload).eq("id", address.id)
    : supabase.from("external_customer_addresses").insert(payload);
  const { data, error } = await request.select("*").single();
  if (error) {
    throw normalizeServiceError(error, "No se pudo guardar la dirección.");
  }
  return data;
};

export const listExternalDeliveryZones = async (branchId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("external_delivery_zones")
    .select("*")
    .eq("branch_id", branchId)
    .order("priority", { ascending: false })
    .order("name", { ascending: true });
  if (error) {
    throw normalizeServiceError(error, "No se pudieron cargar las zonas de delivery.");
  }
  return Array.isArray(data) ? data : [];
};

export const listExternalDeliveryDistanceRates = async (branchId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("external_delivery_distance_rates")
    .select("*")
    .eq("branch_id", branchId)
    .order("min_distance_meters", { ascending: true });

  if (error) {
    if (isMissingDistanceRatesTableError(error)) {
      return { available: false, rates: [] };
    }
    throw normalizeServiceError(error, "No se pudieron cargar las tarifas por distancia.");
  }

  return { available: true, rates: Array.isArray(data) ? data : [] };
};

export const getExternalDeliveryCoverage = async (branchId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("branch_settings")
    .select(
      "delivery_radius_enabled, delivery_max_distance_km, payment_location_latitude, payment_location_longitude, payment_location_city, payment_location_state, payment_location_reference"
    )
    .eq("branch_id", branchId)
    .maybeSingle();

  if (error) {
    const message = `${error.message || ""}`.toLowerCase();
    if (error.code === "42703" || message.includes("delivery_radius_")) {
      return { available: false, radiusEnabled: false, maxDistanceKm: "" };
    }
    throw normalizeServiceError(error, "No se pudo cargar la cobertura por mapa.");
  }

  const latitude = Number(data?.payment_location_latitude);
  const longitude = Number(data?.payment_location_longitude);
  return {
    available: true,
    radiusEnabled: data?.delivery_radius_enabled === true,
    maxDistanceKm:
      data?.delivery_max_distance_km === null ||
      data?.delivery_max_distance_km === undefined
        ? ""
        : String(data.delivery_max_distance_km),
    originConfigured:
      Number.isFinite(latitude) && Number.isFinite(longitude),
    originReference: [
      data?.payment_location_reference,
      data?.payment_location_city,
      data?.payment_location_state,
    ]
      .filter(Boolean)
      .join(" · "),
  };
};

export const saveExternalDeliveryCoverage = async ({
  branchId,
  radiusEnabled,
  maxDistanceKm,
}) => {
  ensureSupabaseReady();
  const parsedDistance = Number(maxDistanceKm);
  if (radiusEnabled && (!Number.isFinite(parsedDistance) || parsedDistance < 0.1 || parsedDistance > 100)) {
    throw new Error("Indica un radio entre 0,1 y 100 km.");
  }

  const { error } = await supabase.from("branch_settings").upsert({
    branch_id: branchId,
    delivery_radius_enabled: radiusEnabled === true,
    delivery_max_distance_km: radiusEnabled ? parsedDistance : null,
  });

  if (error) {
    const message = `${error.message || ""}`.toLowerCase();
    if (error.code === "42703" || message.includes("delivery_radius_")) {
      throw new Error(
        "Falta aplicar delivery_maps_coverage_phase4_patch.sql en Supabase para usar el radio de reparto."
      );
    }
    throw normalizeServiceError(error, "No se pudo guardar el radio de reparto.");
  }
};

export const saveExternalDeliveryZone = async ({ branchId, zone }) => {
  ensureSupabaseReady();
  const allowGeneralCoverage = zone.allowGeneralCoverage === true;
  const neighborhoods = Array.isArray(zone.neighborhoods)
    ? zone.neighborhoods.map((value) => cleanText(value, 100)).filter(Boolean)
    : [];
  const cities = Array.isArray(zone.cities)
    ? zone.cities.map((value) => cleanText(value, 100)).filter(Boolean)
    : [];
  const payload = {
    branch_id: branchId,
    name: cleanText(zone.name, 100),
    active: zone.active !== false,
    neighborhoods,
    cities,
    flat_fee: Math.max(0, Number(zone.flatFee ?? zone.flat_fee ?? 0)),
    min_order: Math.max(0, Number(zone.minOrder ?? zone.min_order ?? 0)),
    free_from: Math.max(0, Number(zone.freeFrom ?? zone.free_from ?? 0)),
    estimated_minutes: Math.max(1, Math.min(360, Number(zone.estimatedMinutes ?? zone.estimated_minutes ?? 35))),
    priority: Number(zone.priority || 0),
    updated_at: new Date().toISOString(),
  };
  if (!payload.name) {
    throw new Error("La zona necesita un nombre.");
  }
  if (!payload.neighborhoods.length && !payload.cities.length && !allowGeneralCoverage) {
    throw new Error(
      "Indica al menos un barrio o localidad cubierta. Asi Delivery no cobra envios fuera de cobertura."
    );
  }
  const request = zone.id
    ? supabase.from("external_delivery_zones").update(payload).eq("id", zone.id)
    : supabase.from("external_delivery_zones").insert(payload);
  const { data, error } = await request.select("*").single();
  if (error) {
    throw normalizeServiceError(error, "No se pudo guardar la zona de delivery.");
  }
  return data;
};

export const saveExternalDeliveryDistanceRate = async ({ branchId, rate }) => {
  ensureSupabaseReady();
  const displayUnit = rate.unit === "km" ? "km" : "m";
  const factor = displayUnit === "km" ? 1000 : 1;
  const minDistanceMeters = Math.round(Number(rate.minDistance) * factor);
  const maxDistanceMeters = Math.round(Number(rate.maxDistance) * factor);
  const flatFee = Number(rate.fee);

  if (!Number.isFinite(minDistanceMeters) || minDistanceMeters < 0) {
    throw new Error("La distancia inicial debe ser cero o mayor.");
  }
  if (!Number.isFinite(maxDistanceMeters) || maxDistanceMeters <= minDistanceMeters) {
    throw new Error("La distancia final debe ser mayor que la inicial.");
  }
  if (!Number.isFinite(flatFee) || flatFee < 0) {
    throw new Error("El precio del envio debe ser cero o mayor.");
  }

  const payload = {
    branch_id: branchId,
    min_distance_meters: minDistanceMeters,
    max_distance_meters: maxDistanceMeters,
    display_unit: displayUnit,
    flat_fee: flatFee,
    estimated_minutes: Math.max(
      1,
      Math.min(360, Number(rate.estimatedMinutes || 35))
    ),
    active: rate.active !== false,
    updated_at: new Date().toISOString(),
  };
  const request = rate.id
    ? supabase
        .from("external_delivery_distance_rates")
        .update(payload)
        .eq("branch_id", branchId)
        .eq("id", rate.id)
    : supabase.from("external_delivery_distance_rates").insert(payload);
  const { data, error } = await request.select("*").single();

  if (error) {
    if (isMissingDistanceRatesTableError(error)) {
      throw new Error(
        `Falta aplicar ${DELIVERY_DISTANCE_RATES_PATCH_FILE} en Supabase para usar tarifas por distancia.`
      );
    }
    if (`${error.message || ""}`.toLowerCase().includes("superponerse")) {
      throw new Error("Ese rango se superpone con otra tarifa activa. Ajusta los limites.");
    }
    throw normalizeServiceError(error, "No se pudo guardar la tarifa por distancia.");
  }

  return data;
};

export const removeExternalDeliveryDistanceRate = async ({ branchId, rateId }) => {
  ensureSupabaseReady();
  const { error } = await supabase
    .from("external_delivery_distance_rates")
    .delete()
    .eq("branch_id", branchId)
    .eq("id", rateId);

  if (error) {
    if (isMissingDistanceRatesTableError(error)) {
      throw new Error(
        `Falta aplicar ${DELIVERY_DISTANCE_RATES_PATCH_FILE} en Supabase para usar tarifas por distancia.`
      );
    }
    throw normalizeServiceError(error, "No se pudo eliminar la tarifa por distancia.");
  }
};

export const listExternalSalesChannels = async (branchId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("external_sales_channels")
    .select("*")
    .eq("branch_id", branchId)
    .order("channel_code", { ascending: true });
  if (error) {
    throw normalizeServiceError(error, "No se pudieron cargar los canales de venta.");
  }
  return Array.isArray(data) ? data : [];
};

export const saveExternalSalesChannel = async ({ branchId, channelCode, active, settings = {} }) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("external_sales_channels")
    .upsert(
      {
        branch_id: branchId,
        channel_code: channelCode,
        active: active === true,
        settings,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "branch_id,channel_code" }
    )
    .select("*")
    .single();
  if (error) {
    throw normalizeServiceError(error, "No se pudo actualizar el canal de venta.");
  }
  return data;
};

export const getExternalWhatsAppSetup = async (branchId) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("external_whatsapp_integrations")
    .select("*")
    .eq("branch_id", branchId)
    .maybeSingle();
  if (error) {
    throw normalizeServiceError(error, "No se pudo cargar la configuración de WhatsApp.");
  }
  return data || null;
};

const readEdgeFunctionError = async (error, fallbackMessage) => {
  let message = error?.message || fallbackMessage;

  if (error?.context) {
    try {
      const payload = await error.context.json();
      message = payload?.error || message;
    } catch {
      // The gateway can return a non-JSON response while an Edge Function starts.
    }
  }

  return message;
};

// The app and signup configuration are public identifiers, but Mozzo returns
// them only to an authenticated operator allowed to manage this branch.
export const getExternalWhatsAppEmbeddedSignupConfig = async ({ branchId }) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.functions.invoke(
    "whatsapp-embedded-signup-config",
    { body: { branchId } }
  );

  if (error) {
    throw new Error(
      await readEdgeFunctionError(
        error,
        "No pudimos preparar la conexion segura de WhatsApp."
      )
    );
  }

  if (!data?.ok) {
    throw new Error(
      data?.error || "No pudimos preparar la conexion segura de WhatsApp."
    );
  }

  return data;
};

// The temporary Meta code is sent directly to the Edge Function. It never
// reaches a table, local storage, or any other persistent browser state.
export const completeExternalWhatsAppEmbeddedSignup = async ({
  branchId,
  code,
  wabaId,
  phoneNumberId,
  finishEvent,
  expectedPhone,
}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.EXTERNAL_NOTIFICATIONS);
  ensureSupabaseReady();
  const { data, error } = await supabase.functions.invoke(
    "whatsapp-embedded-signup-complete",
    {
      body: {
        branchId,
        code,
        wabaId,
        phoneNumberId,
        finishEvent,
        ...(expectedPhone ? { expectedPhone } : {}),
      },
    }
  );

  if (error) {
    throw new Error(
      await readEdgeFunctionError(
        error,
        "No pudimos completar la conexion de WhatsApp."
      )
    );
  }

  if (!data?.ok) {
    throw new Error(data?.error || "No pudimos completar la conexion de WhatsApp.");
  }

  return data;
};

// Exposes only operational activity. The UI receives the sanitized provider error, never the full payload.
export const listExternalWhatsAppWebhookEvents = async ({ branchId, limit = 8 }) => {
  ensureSupabaseReady();
  const { data, error } = await supabase
    .from("external_whatsapp_webhook_events")
    .select("id, direction, event_type, created_at, payload")
    .eq("branch_id", branchId)
    .order("created_at", { ascending: false })
    .limit(Math.max(1, Math.min(20, Number(limit || 8))));

  if (error) {
    const message = getErrorText(error);
    if (error?.code === "42P01" || message.includes("external_whatsapp_webhook_events")) {
      return [];
    }
    throw normalizeServiceError(error, "No se pudo leer la actividad de WhatsApp.");
  }

  return Array.isArray(data)
    ? data.map((event) => ({
        id: event.id,
        direction: event.direction,
        event_type: event.event_type,
        created_at: event.created_at,
        // Only outbound failures have a useful operational reason to display.
        error_message:
          event.event_type === "send_error"
            ? cleanText(event?.payload?.error, 280)
            : "",
      }))
    : [];
};

export const saveExternalWhatsAppSetup = async ({ branchId, setup }) => {
  ensureSupabaseReady();
  const existingToken = cleanText(setup.deliveryPublicToken, 80);
  const wantsLivePreparation = setup.active === true;
  const { data, error } = await supabase
    .from("external_whatsapp_integrations")
    .upsert(
      {
        branch_id: branchId,
        provider: "meta_cloud",
        active: wantsLivePreparation,
        display_name: cleanText(setup.displayName, 100) || null,
        delivery_menu_enabled: setup.deliveryMenuEnabled !== false,
        chatbot_enabled: setup.chatbotEnabled !== false,
        registration_required: setup.registrationRequired !== false,
        delivery_public_token:
          existingToken || createExternalDeliveryPublicToken(),
        welcome_message: cleanText(setup.welcomeMessage, 800) || null,
        unavailable_message: cleanText(setup.unavailableMessage, 800) || null,
        handoff_message: cleanText(setup.handoffMessage, 800) || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "branch_id" }
    )
    .select("*")
    .single();
  if (error) {
    throw normalizeServiceError(error, "No se pudo guardar la configuración de WhatsApp.");
  }
  return data;
};

// La suscripcion a Meta se resuelve en una Edge Function para no exponer el
// token de WhatsApp Business en ningun navegador del restaurante.
export const subscribeExternalWhatsAppWebhook = async ({ branchId }) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.EXTERNAL_NOTIFICATIONS);
  ensureSupabaseReady();
  const { data, error } = await supabase.functions.invoke(
    "whatsapp-delivery-subscribe",
    { body: { branchId } }
  );

  if (error) {
    throw new Error(
      await readEdgeFunctionError(error, "No pudimos asociar Meta a este canal.")
    );
  }

  if (!data?.ok) {
    throw new Error(data?.error || "Meta no confirmo la asociacion del canal.");
  }

  return data;
};

// El numero de prueba solo viaja a la Function durante el diagnostico. Nunca
// se persiste como credencial ni se expone el token de Meta al navegador.
export const sendExternalWhatsAppTestMessage = async ({ branchId, recipient, mode = "text" }) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.EXTERNAL_NOTIFICATIONS);
  ensureSupabaseReady();
  const { data, error } = await supabase.functions.invoke(
    "whatsapp-delivery-test-send",
    { body: { branchId, recipient, mode } }
  );

  if (error) {
    throw new Error(
      await readEdgeFunctionError(error, "No pudimos enviar la prueba de WhatsApp.")
    );
  }

  if (!data?.ok) {
    throw new Error(data?.error || "Meta no confirmo el envio de prueba.");
  }

  return data;
};

export const createExternalWhatsAppTemplate = async ({
  branchId,
  name,
  language = "es_AR",
  body,
}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.EXTERNAL_NOTIFICATIONS);
  ensureSupabaseReady();
  const { data, error } = await supabase.functions.invoke(
    "whatsapp-template-create",
    { body: { branchId, name, language, body } }
  );

  if (error) {
    throw new Error(
      await readEdgeFunctionError(
        error,
        "No pudimos crear la plantilla de WhatsApp."
      )
    );
  }

  if (!data?.ok) {
    throw new Error(data?.error || "Meta no confirmo la creacion de la plantilla.");
  }

  return data;
};

const loadOrderItemsByOrderId = async (orderIds) => {
  if (!orderIds.length) {
    return [];
  }
  const { data, error } = await supabase
    .from("external_order_items")
    .select("*")
    .in("external_order_id", orderIds)
    .order("created_at", { ascending: true });
  if (error) {
    throw normalizeServiceError(error, "No se pudieron cargar los productos de los pedidos.");
  }
  return Array.isArray(data) ? data : [];
};

const loadDeliveryAssignmentsByOrderId = async (orderIds) => {
  if (!orderIds.length) return [];

  const { data, error } = await supabase
    .from("we_delivery_assignments")
    .select(
      "id, external_order_id, branch_id, driver_id, status, tracking_token, estimated_arrival_at, dispatch_available_at, assigned_at, pickup_deadline_at, pickup_arrived_at, pickup_verification_code, pickup_verification_attempts, pickup_verified_at, pickup_verification_locked_at, picked_up_at, delivered_at, cancelled_at, updated_at"
    )
    .in("external_order_id", orderIds);

  if (error) {
    const errorText = getErrorText(error);
    if (
      ["42P01", "PGRST205"].includes(error?.code) ||
      errorText.includes("we_delivery_assignments")
    ) {
      return [];
    }
    throw normalizeServiceError(
      error,
      "No se pudo cargar el estado de asignacion de los repartidores."
    );
  }

  const assignments = Array.isArray(data) ? data : [];
  const assignmentsMissingSecurity = assignments.filter(
    (assignment) =>
      assignment?.driver_id &&
      [
        "driver_assigned",
        "arriving_pickup",
        "ready_for_pickup",
        "arrived_pickup",
      ].includes(assignment?.status) &&
      (!assignment?.pickup_verification_code || !assignment?.pickup_deadline_at)
  );

  if (!assignmentsMissingSecurity.length) return assignments;

  // Repair legacy assignments in the background. The delivery board must not
  // wait for this maintenance RPC before showing the restaurant's orders.
  void Promise.all(
    assignmentsMissingSecurity.map((assignment) =>
      supabase.rpc("ensure_we_delivery_pickup_security", {
        p_order_id: assignment.external_order_id,
      })
    )
  ).catch(() => {
    // The four-second order refresh retries after transient errors.
  });

  return assignments;
};

const ACTIVE_DELIVERY_ASSIGNMENT_STATUSES = new Set([
  "driver_assigned",
  "arriving_pickup",
  "ready_for_pickup",
  "arrived_pickup",
  "picked_up",
  "in_transit",
]);

export const hasExternalDeliveryDriverAssignment = (order) => {
  const assignment = order?.delivery_assignment;
  return Boolean(
    assignment?.driver_id ||
      order?.driver_name ||
      ACTIVE_DELIVERY_ASSIGNMENT_STATUSES.has(assignment?.status)
  );
};

export const isExternalDeliveryAssignmentAvailable = (
  order,
  now = Date.now()
) => {
  if (order?.fulfillment_type !== "delivery") return false;
  if (order?.status === "ready_for_delivery") return true;
  if (order?.status !== "preparing") return false;

  const availableAt = new Date(
    order?.delivery_assignment?.dispatch_available_at || ""
  ).getTime();
  return Number.isFinite(availableAt) && availableAt <= Number(now);
};

export const listExternalOrders = async ({
  branchId,
  mode = "active",
  search = "",
  fromDate = "",
  toDate = "",
  limit = 120,
}) => {
  ensureSupabaseReady();
  let request = supabase
    .from("external_orders")
    .select(
      "*, customer:external_customers!external_orders_customer_id_fkey(*), address:external_customer_addresses!external_orders_delivery_address_id_fkey(*), zone:external_delivery_zones!external_orders_delivery_zone_id_fkey(*)"
    )
    .eq("branch_id", branchId)
    .order("created_at", { ascending: false })
    .limit(Math.max(1, Math.min(300, Number(limit || 120))));
  const activeStatuses = [
    "draft",
    "pending_confirmation",
    "received",
    "pending_payment",
    "confirmed",
    "preparing",
    "ready_for_pickup",
    "ready_for_delivery",
    "assigned_to_driver",
    "out_for_delivery",
  ];

  if (mode === "active") {
    request = request.in("status", activeStatuses);
  } else if (mode === "history") {
    request = request.in("status", ["delivered", "picked_up", "cancelled", "rejected", "refunded"]);
  }
  if (fromDate) {
    request = request.gte("created_at", `${fromDate}T00:00:00`);
  }
  if (toDate) {
    request = request.lte("created_at", `${toDate}T23:59:59.999`);
  }
  const normalizedSearch = cleanText(search, 80).replace(/[,%()]/g, " ").trim();
  if (normalizedSearch) {
    request = request.or(
      `display_number.ilike.%${normalizedSearch}%,customer_notes.ilike.%${normalizedSearch}%,internal_notes.ilike.%${normalizedSearch}%`
    );
  }
  const { data, error } = await request;
  if (error) {
    throw normalizeServiceError(error, "No se pudieron cargar los pedidos externos.");
  }
  const orders = (Array.isArray(data) ? data : []).filter(
    (order) =>
      !(
        mode === "active" &&
        order?.fulfillment_type === "delivery" &&
        order?.payment_method === "mercado_pago" &&
        order?.status === "pending_payment" &&
        order?.payment_status !== "paid"
      )
  );
  const orderIds = orders.map((order) => order.id);
  const [items, deliveryAssignments] = await Promise.all([
    loadOrderItemsByOrderId(orderIds),
    loadDeliveryAssignmentsByOrderId(orderIds),
  ]);
  const itemsByOrder = items.reduce((collection, item) => {
    const current = collection[item.external_order_id] || [];
    current.push(item);
    collection[item.external_order_id] = current;
    return collection;
  }, {});
  const assignmentByOrder = deliveryAssignments.reduce((collection, assignment) => {
    collection[assignment.external_order_id] = assignment;
    return collection;
  }, {});

  return orders.map((order) => ({
    ...order,
    items: itemsByOrder[order.id] || [],
    delivery_assignment: assignmentByOrder[order.id] || null,
  }));
};

export const listExternalOrderNotificationCandidates = async (branchId) => {
  ensureSupabaseReady();

  const { data, error } = await supabase
    .from("external_orders")
    .select(
      "id, display_number, status, source_channel, fulfillment_type, payment_method, payment_status, total_amount, created_at, customer:external_customers!external_orders_customer_id_fkey(first_name, last_name)"
    )
    .eq("branch_id", branchId)
    .in("status", [
      "pending_confirmation",
      "received",
      "pending_payment",
      "confirmed",
      "preparing",
      "ready_for_pickup",
      "ready_for_delivery",
      "assigned_to_driver",
      "out_for_delivery",
    ])
    .order("created_at", { ascending: false })
    .limit(120);

  if (error) {
    throw normalizeServiceError(
      error,
      "No se pudieron revisar los pedidos nuevos de Delivery."
    );
  }

  return (Array.isArray(data) ? data : []).filter(
    (order) =>
      !(
        order?.fulfillment_type === "delivery" &&
        order?.payment_method === "mercado_pago" &&
        order?.status === "pending_payment" &&
        order?.payment_status !== "paid"
      )
  );
};

export const getExternalOrderTimeline = async (orderId) => {
  ensureSupabaseReady();
  const [historyResult, paymentsResult, auditResult] = await Promise.all([
    supabase
      .from("external_order_status_history")
      .select("*")
      .eq("external_order_id", orderId)
      .order("changed_at", { ascending: false }),
    supabase
      .from("external_order_payments")
      .select("*")
      .eq("external_order_id", orderId)
      .order("recorded_at", { ascending: false }),
    supabase
      .from("external_order_audit_events")
      .select("*")
      .eq("external_order_id", orderId)
      .order("created_at", { ascending: false }),
  ]);
  const resultError = historyResult.error || paymentsResult.error || auditResult.error;
  if (resultError) {
    throw normalizeServiceError(resultError, "No se pudo cargar la auditoría del pedido.");
  }
  return {
    statuses: historyResult.data || [],
    payments: paymentsResult.data || [],
    auditEvents: auditResult.data || [],
  };
};

export const createExternalOrder = async ({
  branchId,
  customer,
  address,
  deliveryZoneId = null,
  fulfillmentType,
  paymentMethod,
  cashReceived = null,
  customerNotes = "",
  internalNotes = "",
  items,
  sourceChannel = "manual",
  idempotencyKey = null,
  promotionPricing = null,
}) => {
  ensureSupabaseReady();
  const validItems = (Array.isArray(items) ? items : [])
    .filter((item) => item?.productId && Number(item?.quantity || 0) > 0)
    .map((item) => ({
      productId: item.productId,
      quantity: Math.max(1, Math.min(100, Number(item.quantity || 1))),
      selectionDetails: Array.isArray(item.selectionDetails) ? item.selectionDetails : [],
      ...(item.pizzaSizeOptionId ? {
        pizzaSizeOptionId: item.pizzaSizeOptionId,
        pizzaPortionMode: item.pizzaPortionMode || "full",
        linkedSelections: item.linkedSelections || [],
      } : {}),
      stockConsumptions: Array.isArray(item.stockConsumptions)
        ? item.stockConsumptions
        : [],
      notes: cleanText(item.notes, 500),
    }));

  if (!validItems.length) {
    throw new Error("Agrega al menos un producto antes de confirmar el pedido.");
  }

  const rpcName =
    fulfillmentType === "delivery" && address?.deliveryFeeOverride != null
      ? "create_external_order_with_delivery_fee"
      : "create_external_order";
  const request = {
    p_branch_id: branchId,
    p_customer: {
      firstName: cleanText(customer?.firstName, 80),
      lastName: cleanText(customer?.lastName, 80),
      phone: cleanText(customer?.phone, 60),
      email: cleanText(customer?.email, 160),
      marketingOptIn: customer?.marketingOptIn === true,
      internalNotes: cleanText(customer?.internalNotes, 1200),
    },
    p_address: {
      label: cleanText(address?.label, 60),
      street: cleanText(address?.street, 160),
      streetNumber: cleanText(address?.streetNumber, 40),
      floor: cleanText(address?.floor, 30),
      unit: cleanText(address?.unit, 30),
      neighborhood: cleanText(address?.neighborhood, 100),
      city: cleanText(address?.city, 100),
      province: cleanText(address?.province, 100),
      postalCode: cleanText(address?.postalCode, 30),
      reference: cleanText(address?.reference, 400),
      deliveryDistanceMeters:
        address?.deliveryDistanceMeters === "" || address?.deliveryDistanceMeters == null
          ? null
          : Math.max(0, Number(address.deliveryDistanceMeters)),
      deliveryRouteDurationSeconds:
        address?.deliveryRouteDurationSeconds === "" ||
        address?.deliveryRouteDurationSeconds == null
          ? null
          : Math.max(0, Number(address.deliveryRouteDurationSeconds)),
      deliveryFeeOverride:
        address?.deliveryFeeOverride === "" || address?.deliveryFeeOverride == null
          ? null
          : Math.max(0, Number(address.deliveryFeeOverride)),
    },
    p_delivery_zone_id: deliveryZoneId || null,
    p_fulfillment_type: fulfillmentType,
    p_payment_method: paymentMethod,
    p_cash_received:
      cashReceived === "" || cashReceived == null ? null : Math.max(0, Number(cashReceived)),
    p_customer_notes: cleanText(customerNotes, 1200),
    p_internal_notes: cleanText(internalNotes, 1200),
    p_items: validItems,
    p_source_channel: sourceChannel,
    p_idempotency_key: idempotencyKey || null,
  };
  const { data, error } = promotionPricing?.paymentPrices
    ? await supabase.rpc("create_external_order_with_promotion", { p_request: request, p_pricing: promotionPricing })
    : await supabase.rpc(rpcName, request);
  if (error) {
    throw normalizeServiceError(error, "No se pudo registrar el pedido externo.");
  }
  return data;
};

export const transitionExternalOrderStatus = async ({ orderId, nextStatus, note = "" }) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc("transition_external_order_status", {
    p_order_id: orderId,
    p_next_status: nextStatus,
    p_note: cleanText(note, 1000) || null,
  });
  if (error) {
    throw normalizeServiceError(error, "No se pudo actualizar el estado del pedido.");
  }
  return data;
};

export const resolveExternalOrderCustomerRequest = async ({
  orderId,
  resolution,
  note = "",
}) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc(
    "resolve_external_order_customer_request",
    {
      p_order_id: orderId,
      p_resolution: resolution,
      p_note: cleanText(note, 1000) || null,
    }
  );
  if (error) {
    throw normalizeServiceError(
      error,
      "No se pudo responder la solicitud del cliente."
    );
  }
  return data;
};

export const assignExternalOrderDriver = async ({ orderId, driverName, driverPhone = "" }) => {
  ensureSupabaseReady();
  const safeDriverName = cleanText(driverName, 120);

  if (!safeDriverName) {
    throw new Error("Indica el nombre del repartidor antes de asignar el pedido.");
  }

  const { data, error } = await supabase.rpc("assign_external_order_driver", {
    p_order_id: orderId,
    p_driver_name: safeDriverName,
    p_driver_phone: cleanText(driverPhone, 60) || null,
  });
  if (error) {
    throw normalizeServiceError(error, "No se pudo asignar el repartidor.");
  }
  return data;
};

export const offerExternalOrderToWeRiders = async ({ orderId }) => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc("offer_external_order_to_we_riders", {
    p_order_id: orderId,
  });
  if (error) {
    throw normalizeServiceError(
      error,
      "No se pudo publicar el pedido para los riders disponibles."
    );
  }
  return data;
};

export const releaseExpiredWeRiderAssignment = async ({ orderId }) => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc(
    "release_expired_we_delivery_assignment",
    { p_order_id: orderId }
  );
  if (error) {
    throw normalizeServiceError(
      error,
      "No se pudo liberar al Rider que excedió el tiempo de llegada."
    );
  }
  return data;
};

export const recordExternalOrderPayment = async ({
  orderId,
  paymentStatus,
  providerReference = "",
  note = "",
}) => {
  ensureSupabaseReady();
  const { data, error } = await supabase.rpc("record_external_order_payment", {
    p_order_id: orderId,
    p_payment_status: paymentStatus,
    p_provider_reference: cleanText(providerReference, 160) || null,
    p_note: cleanText(note, 500) || null,
  });
  if (error) {
    throw normalizeServiceError(error, "No se pudo registrar el cobro.");
  }
  return data;
};

const toOperationalPhase = (status) => {
  if (["preparing", "confirmed"].includes(status)) return "preparation";
  if (["ready_for_pickup", "ready_for_delivery", "assigned_to_driver", "out_for_delivery"].includes(status)) return "ready";
  return "new";
};

const hasMenuProducts = (menuSnapshot) =>
  Array.isArray(menuSnapshot?.products) && menuSnapshot.products.length > 0;

export const enrichExternalOperationalItemsWithMenu = (
  items = [],
  menuSnapshot = null
) => {
  if (!hasMenuProducts(menuSnapshot)) {
    return Array.isArray(items) ? items : [];
  }

  const productsById = new Map(
    menuSnapshot.products.map((product) => [String(product?.id || ""), product])
  );

  return (Array.isArray(items) ? items : []).map((item) => {
    const product = productsById.get(
      String(item?.product_id || item?.productId || "")
    );

    if (!product) return item;

    return {
      ...item,
      categoryName: item.categoryName || product.categoryName || "",
      subcategoryName: item.subcategoryName || product.subcategoryName || "",
      sendToKitchen:
        typeof item.sendToKitchen === "boolean"
          ? item.sendToKitchen
          : product.sendToKitchen,
    };
  });
};

export const resolveExternalKitchenDispatchContext = async ({
  branch,
  order,
  menuSnapshot = null,
} = {}) => {
  let effectiveMenuSnapshot = menuSnapshot;
  let effectiveBranch = branch;
  let kitchenMode = resolveOrderKitchenModeAtDispatch({
    order,
    branch,
    profile: branch ? getRestaurantProfile(branch) : null,
  });

  const profilePromise =
    branch?.id && kitchenMode === "manual"
      ? getBranchPublicProfile(branch.id).catch(() => null)
      : Promise.resolve(null);
  const menuPromise =
    branch?.id && !hasMenuProducts(effectiveMenuSnapshot)
      ? getBranchMenuSnapshot(branch.id).catch(() => null)
      : Promise.resolve(null);
  const kitchenSettingsPromise = branch?.id && typeof supabase?.from === "function"
    ? supabase
        .from("branch_settings")
        .select("kitchen_mode")
        .eq("branch_id", branch.id)
        .maybeSingle()
        .then(({ data }) => data || null)
        .catch(() => null)
    : Promise.resolve(null);
  const [remoteProfile, remoteMenuSnapshot, kitchenSettings] = await Promise.all([
    profilePromise,
    menuPromise,
    kitchenSettingsPromise,
  ]);

  if (remoteProfile) {
    effectiveBranch = {
      ...branch,
      kitchenMode: remoteProfile.kitchenMode || branch?.kitchenMode,
    };
    kitchenMode = resolveOrderKitchenModeAtDispatch({
      order,
      branch: effectiveBranch,
      profile: remoteProfile,
    });
  }

  if (hasMenuProducts(remoteMenuSnapshot)) {
    effectiveMenuSnapshot = remoteMenuSnapshot;
  }

  const persistedKitchenMode = cleanText(
    kitchenSettings?.kitchen_mode,
    40
  ).toLowerCase();
  if (["screen", "printer"].includes(persistedKitchenMode)) {
    kitchenMode = persistedKitchenMode;
    effectiveBranch = {
      ...effectiveBranch,
      kitchenMode: persistedKitchenMode,
    };
  }

  if (
    branch?.id &&
    (kitchenMode === "manual" || !hasMenuProducts(effectiveMenuSnapshot))
  ) {
    try {
      const { data: publicMenu, error: publicMenuError } = await supabase.rpc(
        "get_public_kiosk_menu_by_branch",
        {
          p_branch_id: String(branch.id).trim(),
          p_station_number: 1,
        }
      );

      if (!publicMenuError && publicMenu) {
        const publicKitchenMode = resolveOrderKitchenModeAtDispatch({
          order,
          branch: publicMenu.branch,
          profile: publicMenu.branch,
        });

        if (publicKitchenMode === "screen" || publicKitchenMode === "printer") {
          kitchenMode = publicKitchenMode;
          effectiveBranch = {
            ...effectiveBranch,
            kitchenMode: publicKitchenMode,
          };
        }

        if (hasMenuProducts(publicMenu)) {
          effectiveMenuSnapshot = publicMenu;
        }
      }
    } catch {
      // The authenticated profile/menu reads above remain valid fallbacks.
    }
  }

  return {
    branch: effectiveBranch,
    kitchenMode,
    menuSnapshot: effectiveMenuSnapshot,
  };
};

const getExternalOperationalItems = (order, menuSnapshot) => {
  const productsById = new Map(
    (menuSnapshot?.products || []).map((product) => [String(product?.id || ""), product])
  );

  const items = (order.items || []).map((item) => {
    const product = productsById.get(String(item?.product_id || item?.productId || ""));

    return {
      id: `external-item-${item.id}`,
      ...(item.pizza_configuration || {}),
      productId: item.product_id || item.productId || "",
      productName: item.product_name || item.productName || "Producto",
      nombre: item.product_name || item.productName || "Producto",
      quantity: Math.max(1, Number(item.quantity || 1)),
      baseUnitPrice: Number(item.unit_price ?? item.unitPrice ?? 0),
      extraUnitCost: 0,
      unitPrice: Number(item.unit_price ?? item.unitPrice ?? 0),
      delivered: item.delivered === true,
      categoryName: item.categoryName || product?.categoryName || "",
      subcategoryName: item.subcategoryName || product?.subcategoryName || "",
      sendToKitchen:
        typeof item.sendToKitchen === "boolean"
          ? item.sendToKitchen
          : product?.sendToKitchen,
      selectionDetails: Array.isArray(item.selection_details)
        ? item.selection_details
        : Array.isArray(item.selectionDetails)
          ? item.selectionDetails
          : [],
      notes: item.notes || "",
      consumos: Array.isArray(item.stock_consumptions)
        ? item.stock_consumptions
        : Array.isArray(item.stockConsumptions)
          ? item.stockConsumptions
          : [],
    };
  });

  return enrichExternalOperationalItemsWithMenu(items, menuSnapshot);
};

export const buildExternalOperationalOrder = (
  order,
  { branch = null, menuSnapshot = null } = {}
) => {
  const phase = toOperationalPhase(order.status);
  const kitchenModeSnapshot = resolveOrderKitchenModeAtDispatch({
    order,
    branch,
    profile: branch ? getRestaurantProfile(branch) : null,
  });
  const items = getExternalOperationalItems(order, menuSnapshot);
  const requiresKitchen = orderRequiresKitchen({ items });
  const kitchenDispatchStatus =
    phase === "preparation"
      ? !requiresKitchen
        ? "not_required"
        : kitchenModeSnapshot === "screen"
          ? "screen_pending"
          : kitchenModeSnapshot === "printer"
            ? "printer_pending"
            : "manual_confirmed"
      : "pending";

  return {
    id: `external-${order.id}`,
    externalOrderId: order.id,
    mesa:
      order.fulfillment_type === "delivery"
        ? `Delivery · ${order.display_number}`
        : `Retiro · ${order.display_number}`,
    customerName: `${order.customer?.first_name || "Cliente"} ${order.customer?.last_name || ""}`.trim(),
    comensales: 1,
    canal: order.fulfillment_type === "delivery" ? "Delivery" : "Retiro",
    source: "external",
    requestedPaymentMethod: order.payment_method || "",
    selectedPaymentMethod: order.payment_method || "",
    paymentStatus: order.payment_status || "pending",
    cashPaymentPending: order.payment_status !== "paid",
    kitchenModeSnapshot,
    kitchenDispatchStatus,
    phase,
    stockDiscounted: order.stockDiscounted === true,
    orderedAt: order.created_at || new Date().toISOString(),
    notes: [order.customer_notes, order.internal_notes]
      .filter(Boolean)
      .join(" · ")
      .slice(0, 260),
    totalAmount: Number(order.total_amount || 0),
    pricing: {
      listTotal: Number(order.subtotal || 0),
      comboDiscountTotal: 0,
      promotionDiscountTotal: Number(order.discount_amount || 0),
      totalDiscount: Number(order.discount_amount || 0),
      finalTotal: Number(order.total_amount || 0),
      adjustments: [],
    },
    items,
  };
};

const OPERATIONAL_PHASE_RANK = {
  new: 0,
  preparation: 1,
  ready: 2,
  closed: 3,
  cancelled: 3,
};

const mergeExternalOperationalProjection = (currentOrder, projectedOrder) => {
  if (!currentOrder) return projectedOrder;

  const currentRank = OPERATIONAL_PHASE_RANK[currentOrder.phase] ?? 0;
  const projectedRank = OPERATIONAL_PHASE_RANK[projectedOrder.phase] ?? 0;
  const currentItemsById = new Map(
    (currentOrder.items || []).map((item) => [String(item?.id || ""), item])
  );

  return {
    ...currentOrder,
    ...projectedOrder,
    phase: currentRank > projectedRank ? currentOrder.phase : projectedOrder.phase,
    kitchenDispatchStatus:
      currentOrder.kitchenDispatchStatus && currentOrder.kitchenDispatchStatus !== "pending"
        ? currentOrder.kitchenDispatchStatus
        : projectedOrder.kitchenDispatchStatus,
    kitchenDispatchedAt: currentOrder.kitchenDispatchedAt || projectedOrder.kitchenDispatchedAt,
    kitchenDispatchedBy: currentOrder.kitchenDispatchedBy || projectedOrder.kitchenDispatchedBy,
    stockDiscounted: currentOrder.stockDiscounted === true || projectedOrder.stockDiscounted === true,
    items: (projectedOrder.items || []).map((item) => ({
      ...item,
      delivered: currentItemsById.get(String(item?.id || ""))?.delivered === true,
    })),
  };
};

export const dispatchExternalOrderToKitchen = async ({ branch, order, menuSnapshot = null }) => {
  if (!branch?.id || !order?.id) {
    throw new Error("No encontramos la sucursal o el pedido para enviarlo a cocina.");
  }
  const dispatchContext = await resolveExternalKitchenDispatchContext({
    branch,
    order,
    menuSnapshot,
  });
  const operationalOrder = buildExternalOperationalOrder(
    {
      ...order,
      kitchenModeSnapshot: dispatchContext.kitchenMode,
    },
    {
      branch: dispatchContext.branch,
      menuSnapshot: dispatchContext.menuSnapshot,
    }
  );
  if (!orderRequiresKitchen(operationalOrder)) {
    return {
      dispatched: false,
      requiresKitchen: false,
      supported: true,
      operationalOrderId: "",
      statusTransitioned: false,
    };
  }
  const fallbackFactory = () => getOperationalOrdersState(branch, () => ({ mesasActivas: [], pedidosHistoricos: [] }));

  const { data: atomicDispatch, error: atomicDispatchError } = await supabase.rpc(
    "dispatch_external_order_to_kitchen",
    {
      p_order_id: order.id,
      p_operational_order: operationalOrder,
    }
  );
  if (!atomicDispatchError) {
    const synchronizedState = atomicDispatch?.state || null;
    if (synchronizedState) {
      saveOperationalOrdersState(branch, synchronizedState, fallbackFactory);
    }
    return {
      dispatched: true,
      requiresKitchen: true,
      supported: true,
      statusTransitioned: atomicDispatch?.statusTransitioned === true,
      operationalOrderId:
        atomicDispatch?.operationalOrderId || operationalOrder.id,
      operationalOrder:
        atomicDispatch?.operationalOrder || operationalOrder,
    };
  }
  if (!isMissingAtomicKitchenDispatchFunction(atomicDispatchError)) {
    throw normalizeServiceError(
      atomicDispatchError,
      "No pudimos enviar el pedido a Cocina."
    );
  }

  const sharedState = await fetchSharedOperationalState(branch, fallbackFactory);
  const baseState = sharedState.supported ? sharedState.payload : fallbackFactory();
  const currentOperationalOrder = (baseState.mesasActivas || []).find(
    (item) => item?.externalOrderId === order.id || item?.id === operationalOrder.id
  );
  const nextOperationalOrder = mergeExternalOperationalProjection(
    currentOperationalOrder,
    operationalOrder
  );
  const nextState = {
    ...baseState,
    mesasActivas: currentOperationalOrder
      ? (baseState.mesasActivas || []).map((item) =>
          item?.id === currentOperationalOrder.id ? nextOperationalOrder : item
        )
      : [nextOperationalOrder, ...(baseState.mesasActivas || [])],
  };

  saveOperationalOrdersState(branch, nextState, fallbackFactory);
  const pushedState = await pushSharedOperationalState(branch, nextState, fallbackFactory);

  if (!order.operational_order_id) {
    const { error } = await supabase.rpc("link_external_order_to_operational", {
      p_order_id: order.id,
      p_operational_order_id: operationalOrder.id,
    });
    if (error) {
      throw normalizeServiceError(error, "El pedido fue enviado a cocina, pero no pudimos guardar la vinculación.");
    }
  }

  return {
    dispatched: !currentOperationalOrder,
    requiresKitchen: true,
    supported: pushedState.supported,
    statusTransitioned: false,
    operationalOrderId: operationalOrder.id,
    operationalOrder,
  };
};

export const PHASE1_REQUIRED_MIGRATION = PHASE1_PATCH_FILE;
